import { StoreSelectionServiceClient } from '@api/StoreSelectionServiceClient'
import { activate } from '@services/DeviceLocationService'
import _ from 'lodash'
import { IAuthenticatedRequestHandlerResponse } from '@api/utils/AuthenticatedRequestHandler'
import { LocationCheckUtils } from '@utils/LocationCheckUtils'
import { setIsLoggedIn } from '@atredux/slices/ihmLoginTokenInfoSlice'
import { store } from '@atredux/store'
import { cordovaExec } from '@services/CandyCaneService'
import { EnvironmentPluginConstants } from '@utils/Constants/EnvironmentPluginConstants'
import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'

export class StoreIdServiceFor3P {
    private static readonly STORE_ID_KEY = 'storeId'
    private static readonly STORE_FAMILY_KEY = 'storeFamily'
    private static readonly REGION_KEY = 'region'
    private static readonly STORE_DETAILS_KEY = 'storeDetails'

    private static selectedStoreId: string = undefined
    private static storeDetail: any = undefined
    private static storesDetails: any [] = undefined
    private static nearbyStores: string [] = []
    private static deviceLocObj = undefined

    public static getSelectedStoreId (): string {
        return StoreIdServiceFor3P.selectedStoreId
    }

    public static getNearbyStores (): string [] {
        return StoreIdServiceFor3P.nearbyStores
    }

    public static getStoreDetail (): any {
        return window.sessionStorage.getItem(StoreIdServiceFor3P.STORE_DETAILS_KEY)
    }

    public static setRegion (region: string) {
        window.sessionStorage.setItem(StoreIdServiceFor3P.REGION_KEY, region.toLowerCase())
    }
    public static setStoreId (storeId: string) {
        window.sessionStorage.setItem(StoreIdServiceFor3P.STORE_ID_KEY, storeId)
    }

    public static setStoreFamily (storeFamily: string) {
        window.sessionStorage.setItem(StoreIdServiceFor3P.STORE_FAMILY_KEY, storeFamily)
    }

    public static setStoreDetails (storeDetails: any) {
        window.sessionStorage.setItem(StoreIdServiceFor3P.STORE_DETAILS_KEY, JSON.stringify(storeDetails))
    }

    public static async setSelectedStoreIdAndStoreDetail (storeId: string, isStoreIdSetThroughQROrInput: boolean) {
        StoreIdServiceFor3P.selectedStoreId = storeId
        if (isStoreIdSetThroughQROrInput) {
            try {
                const response: IAuthenticatedRequestHandlerResponse = await StoreIdServiceFor3P.getStoreDetailForStoreId(storeId)
                StoreIdServiceFor3P.storeDetail = response.body.store
                console.log('ATBase: StoreSelectionService return storeDetails', JSON.stringify(StoreIdServiceFor3P.storeDetail))
            } catch (error) {
                console.error(error)
                throw new Error('Incorrect store details (or) unauthorized store')
            }
            // Prohibits user to bypass store-selection page if no stores nearby
            if (!_.isUndefined(StoreIdServiceFor3P.deviceLocObj) && StoreIdServiceFor3P.getNearbyStores().indexOf(storeId) === -1) {
                console.log('ATBase: User is outside of store geo-fence radius')
                throw new Error('Your location does not match store location')
            }
        } else {
            StoreIdServiceFor3P.storeDetail = StoreIdServiceFor3P.fetchStoreInfoOfSelectedStoreId(storeId)
            console.log('ATBase: Saved store details ', JSON.stringify(StoreIdServiceFor3P.storesDetails))
        }
        StoreIdServiceFor3P.setStoreDetailsInSessionStorage()

        if (EnvironmentDetail.isAndroid()) {
            await StoreIdServiceFor3P.setStoreDetailsInAndroid()
        }
        // We are setting this here to load IFrame after Store-Selection page
        store.dispatch((setIsLoggedIn(true)))
        // starting location check, This job will run in every 2 minutes
        LocationCheckUtils.init()
    }

    public static async resetStoreIdServiceFor3P () {
        StoreIdServiceFor3P.selectedStoreId = undefined
        StoreIdServiceFor3P.storeDetail = undefined
        StoreIdServiceFor3P.nearbyStores = []
        StoreIdServiceFor3P.storeDetail = undefined
        StoreIdServiceFor3P.deviceLocObj = undefined
        window.sessionStorage.clear()

        if (EnvironmentDetail.isAndroid()) {
            await StoreIdServiceFor3P.clearKeysFromAndroid([StoreIdServiceFor3P.STORE_ID_KEY, StoreIdServiceFor3P.STORE_FAMILY_KEY, StoreIdServiceFor3P.REGION_KEY])
        }
    }

    public static async getNearbyStoresForUser () {
        console.log('ATBase: fetching device location')
        try {
            const deviceLocation: any = await StoreIdServiceFor3P.getDeviceLocation()
            console.log('ATBase: GetNearbyLocation: location received from LocationPlugin: ', JSON.stringify(deviceLocation))
            if (!_.isUndefined(deviceLocation.latitude) && !_.isUndefined(deviceLocation.longitude)) {
                const response: IAuthenticatedRequestHandlerResponse = await StoreSelectionServiceClient.getInstance()
                    .getNearbyStores(String(deviceLocation.latitude), String(deviceLocation.longitude))
                StoreIdServiceFor3P.storesDetails = response.body.stores
                console.log('ATBase: Nearby stores info', JSON.stringify(response.body))
                const nearbyStores = StoreIdServiceFor3P.fetchNearbyStoreIds(StoreIdServiceFor3P.storesDetails)
                StoreIdServiceFor3P.nearbyStores = nearbyStores
            } else {
                throw new Error('Not able to fetch device location')
            }
        } catch (error) {
            console.error('ATBase: StoreSelectionService: error occurred while fetching nearbyStores', error)
            throw new Error('Unable to fetch nearby stores, Please scan QR code or Manually select store')
        }
        return StoreIdServiceFor3P.nearbyStores
    }

    public static async getStoreDetailForStoreId (storeId: string) {
        console.log('ATBase: fetching storeDetails of storeId ', storeId)
        try {
            const storeDetail: any = await StoreSelectionServiceClient.getInstance()
                .getStoreDetailsByStoreId(storeId)
            return storeDetail
        } catch (error) {
            console.error('ATBase: StoreSelectionService: Error occurred while fetching storeDetails', error)
            throw error
        }
    }

    public static setStoreDetailsInSessionStorage () {
        const storeDetails = { ...StoreIdServiceFor3P.storeDetail, lastSuccessfulLocationFetch: new Date().getTime() }
        StoreIdServiceFor3P.setStoreDetails(storeDetails)
        StoreIdServiceFor3P.setRegion(storeDetails.region)
        StoreIdServiceFor3P.setStoreFamily(storeDetails.storeFamily)
        StoreIdServiceFor3P.setStoreId(storeDetails.storeId)
    }

    private static async getDeviceLocation () {
        try {
            console.log('ATBase: activating device location')
            const currentDeviceLocation: any = await activate()
            console.log('ATBase: GetDeviceLocation: location received from LocationPlugin: ', JSON.stringify(currentDeviceLocation))
            if (!_.isUndefined(currentDeviceLocation) && currentDeviceLocation.Accuracy < 50) {
                StoreIdServiceFor3P.deviceLocObj = currentDeviceLocation
                return currentDeviceLocation
            }
        } catch (error) {
            console.error('ATBase: Error while fetching location', error)
            throw error
        }
    }

    private static fetchNearbyStoreIds (storeDetailsList: any) {
        if (storeDetailsList && storeDetailsList.length === 0) {
            console.log('ATBase: No Nearby stores in Users location')
            return []
        }
        const storeIdList: string [] = storeDetailsList.map((storeInfo) => {
            return storeInfo.storeId
        })
        console.log('ATBase: Nearby store list ', storeIdList)
        return storeIdList
    }

    private static fetchStoreInfoOfSelectedStoreId (storeId: string) {
        const storeDetail: any = StoreIdServiceFor3P.storesDetails.filter((storeInfo) => {
            return !storeInfo.storeId.localeCompare(storeId)
        }).map((storeInfo) => {
            return storeInfo
        })[0]
        return storeDetail
    }

    private static async setStoreDetailsInAndroid () {
        const storeDetailsForAndroid = (({ storeId, storeFamily, region }) => ({ storeId, storeFamily, region }))(StoreIdServiceFor3P.storeDetail)
        storeDetailsForAndroid.region = StoreIdServiceFor3P.storeDetail.region.toLowerCase()

        await cordovaExec(EnvironmentPluginConstants.ENVIRONMENT_PLUGIN_NAME,
            EnvironmentPluginConstants.ACTION_SET_STORE_DETAILS,
            [storeDetailsForAndroid])
    }

    private static async clearKeysFromAndroid (keys: String[]) {
        for (const key of keys) {
            await cordovaExec(EnvironmentPluginConstants.ENVIRONMENT_PLUGIN_NAME,
                EnvironmentPluginConstants.ACTION_CLEAR_SHAREDPREFS_KEY,
                [key])
        }
    }
}
